import {
    backgroundColor,
    borderColor,
    fontSize,
    lineHeight,
    primitiveColors,
    radius,
    spacing,
    textColor,
} from '../../theming';

// the theme has no monospace token; VINs, job names and model ids on this page share this stack
export const monoFontFamily = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/** The page's white rounded card (status strip, and the stats and batches sections that follow). */
export const cardStyle = {
    backgroundColor: backgroundColor.white,
    border: `1px solid ${primitiveColors.gray75}`,
    borderRadius: radius.lg,
    padding: `${spacing[2]} ${spacing[3]}`,
};

export const monoChipStyle = {
    fontFamily: monoFontFamily,
    fontSize: fontSize.xxs,
    lineHeight: lineHeight.sm,
    color: textColor.primary,
    backgroundColor: primitiveColors.gray25,
    border: `1px solid ${borderColor.primary}`,
    borderRadius: radius.sm,
    padding: `0 ${spacing[0.5]}`,
};

export const monoChipWarningStyle = {
    ...monoChipStyle,
    color: textColor.warning,
    borderColor: borderColor.warning,
};

export const labelStyle = { fontSize: fontSize.xs, lineHeight: lineHeight.md, color: textColor.secondaryAlt };

export const valueStyle = {
    fontSize: fontSize.xs,
    lineHeight: lineHeight.md,
    color: textColor.primary,
    fontWeight: 500,
};

/** A card's title (its one-line muted subtitle is `mutedSmallStyle`). */
export const cardTitleStyle = {
    fontSize: fontSize.sm,
    lineHeight: lineHeight.lg,
    fontWeight: 600,
    color: textColor.primary,
};

/** Read by screen readers, not shown — for meaning carried only by a glyph or a colour (▲/▼, "—"). */
export const visuallyHiddenStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

/** A small coloured legend/status dot in any colour. */
export const colorDotStyle = color => ({
    display: 'inline-block',
    flexShrink: 0,
    width: spacing[1],
    height: spacing[1],
    borderRadius: radius.huge,
    backgroundColor: color,
});

/** Small muted text: card subtitles, sub-labels under a cell value or a stat, notes, "#carId". */
export const mutedSmallStyle = { fontSize: fontSize.xxs, lineHeight: lineHeight.sm, color: textColor.secondaryAlt };

export const monoTextStyle = { fontFamily: monoFontFamily, fontSize: fontSize.xxs, lineHeight: lineHeight.sm };

/** A red mono tag for a validation error code. */
export const errorCodeTagStyle = { ...monoTextStyle, marginInlineEnd: spacing[0.5] };

/** Row backgrounds: a stuck batch (light orange) and a failed car (light red). */
export const stuckRowBackground = backgroundColor.nectarine;
export const failedRowBackground = primitiveColors.red50;

/** The grey inset card of an expanded batch row ("Cars in this batch"). */
export const insetCardStyle = {
    backgroundColor: primitiveColors.gray25,
    border: `1px solid ${primitiveColors.gray75}`,
    borderRadius: radius.md,
    padding: spacing[3],
};

/** The drawer's grey info box (Stock no. / Batch / Current VIN). */
export const infoBoxStyle = {
    backgroundColor: primitiveColors.gray25,
    borderRadius: radius.md,
    padding: spacing[2],
};

/** One attempt card in the drawer; the latest attempt of a failed car gets a red border. */
export const attemptCardStyle = highlighted => ({
    border: `1px solid ${highlighted ? borderColor.error : primitiveColors.gray75}`,
    borderRadius: radius.md,
    padding: spacing[2],
    backgroundColor: backgroundColor.white,
});

export const candidateVinStyle = {
    fontFamily: monoFontFamily,
    fontSize: fontSize.md,
    lineHeight: lineHeight.lg,
    fontWeight: 600,
    color: textColor.primary,
    wordBreak: 'break-all',
};

/** The threshold marker over the confidence bar, at `percent` (0–100) of its width. */
export const thresholdMarkerStyle = percent => ({
    position: 'absolute',
    top: '-2px',
    bottom: '-2px',
    left: `${percent}%`,
    width: '2px',
    backgroundColor: textColor.primary,
    borderRadius: radius.sm,
});

/** Status dot colour per processing state: green only while running. */
const stateColor = {
    running: textColor.success,
    paused: textColor.warning,
    polling_paused: textColor.warning,
    disabled_by_env: textColor.error,
};

export const dotStyle = state => ({
    display: 'inline-block',
    width: spacing[1],
    height: spacing[1],
    borderRadius: radius.huge,
    backgroundColor: stateColor[state],
});
